import { type FC, useDeferredValue, useState } from 'react';
import { Controller } from 'react-hook-form';
import { SearchableInputSelectOpenSearch } from '@components/SearchableInputSelectOpenSearch';
import { ResourceState } from '@domain/schemas/contract.schema';
import { useSubmissionsOpensearch } from '@queries/fetchSubmissions';
import { isEmptyValue } from '@root/helpers';
import { type SubmissionShort } from '@src/queries';

import type { StepProps } from '../types';
import { resetForParentSubmission } from '../utils';

function filterFinalSubmissions(item: SubmissionShort) {
  return !isEmptyValue(item.name) && item.status === ResourceState.FINAL;
}

export const Step0OpenSearch: FC<StepProps> = ({ control, formValues, reset }) => {
  const [searchText, setSearchText] = useState('');
  const deferredSearchText = useDeferredValue(searchText);
  const {
    data: submissionsPaginated,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useSubmissionsOpensearch({
    queryFilters: [{ field: 'status', comparator: 'eq', values: ['FINAL'] }],
    search: deferredSearchText,
  });

  const submissions = submissionsPaginated?.pages.flatMap((page) => page.data) ?? [];

  const finalSubmissions = submissions.filter(filterFinalSubmissions).map((item) => ({
    name: item.name,
    value: item.id,
  }));

  return (
    <Controller
      control={control}
      name="parentSubmissionId"
      render={({ field, fieldState: { error } }) => (
        <SearchableInputSelectOpenSearch
          {...field}
          isLoading={isLoading}
          labelText="Select the contract to endorse"
          placeholder="Search contracts"
          options={finalSubmissions}
          value={formValues.parentSubmissionId}
          onChange={(value) => {
            field.onChange(value);
            resetForParentSubmission(reset, value as string | undefined);
          }}
          noOptionsLeftLabel="No contracts available"
          errors={error?.message}
          dataTestId="select:parentSubmissionId"
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          searchText={searchText}
          setSearchText={setSearchText}
          hasNextPage={hasNextPage}
        />
      )}
    />
  );
};
